import React, { useEffect, useState } from 'react';

import { Drawer, Descriptions, Spin, Tag, Space, Typography, Divider, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '@utils/dateTime';

import ProductSerialNumberTimeline from './ProductSerialNumberTimeline';
import type { ProductSerialNumberDetail } from '@services/shared/productSerialNumberSchemas';

const { Title, Text } = Typography;

// ----------------------------------------------------------------------------
// Drawer UI 沿用 antd Drawer (參考 ProductSerialNumbersDrawer.tsx / ReturnVerificationDrawer.tsx)
//
// 載入流程：
//   - 開啟 (open === true) 且有 psnId 時，呼叫 service.getById 取 detail
//   - 切換 psnId / 關閉時 cancel 舊 request (用 cancelled flag)
//   - loading 期間顯示 Spin；data ready 後渲染 Descriptions + Timeline
// ----------------------------------------------------------------------------

// caller 只需提供 getById 介面，與 ProductSerialNumberListProps.service 兼容
interface PsnDrawerService {
  getById: (id: string) => Promise<unknown>;
}

export interface ProductSerialNumberDrawerProps {
  role: 'admin' | 'operator';
  service: PsnDrawerService;
  psnId: string | null;
  open: boolean;
  onClose: () => void;
}

const ProductSerialNumberDrawer: React.FC<ProductSerialNumberDrawerProps> = ({
  role,
  service,
  psnId,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ProductSerialNumberDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !psnId) {
      // close → 清掉舊 state，下次重開不會閃舊資料
      setDetail(null);
      setErrorKey(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setDetail(null);

    service
      .getById(psnId)
      .then((response) => {
        if (cancelled) return;
        const envelope = response as {
          success?: boolean;
          message?: string;
          data?: ProductSerialNumberDetail;
        };
        if (envelope?.success && envelope.data) {
          setDetail(envelope.data);
        } else {
          // BE 雖然 200 但 success=false (或拿不到 data) 的 fallback
          setErrorKey(
            envelope?.message
              ? `errors.${envelope.message}`
              : 'shared.product_serial_numbers.drawer.errors.fetch_failed',
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setErrorKey('shared.product_serial_numbers.drawer.errors.fetch_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, psnId, service]);

  const placeholder = t('shared.product_serial_numbers.drawer.no_inventory_placeholder', '—');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      title={
        detail ? (
          <Space size={8} align="center">
            <Text strong data-testid="psn-drawer-title">
              {detail.number}
            </Text>
            <Tag data-testid={`psn-drawer-status-${detail.status}`}>
              {t(`shared.product_serial_numbers.filters.status.${detail.status}`)}
            </Tag>
          </Space>
        ) : (
          t('shared.product_serial_numbers.drawer.title')
        )
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      )}

      {!loading && errorKey && (
        <Alert
          type="error"
          showIcon
          message={t(errorKey)}
          data-testid="psn-drawer-error"
        />
      )}

      {!loading && detail && (
        <>
          <Title level={5} style={{ marginTop: 0 }}>
            {t('shared.product_serial_numbers.drawer.sections.info')}
          </Title>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('shared.product_serial_numbers.columns.product')}>
              <div>{detail.product.name}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {detail.product.sku}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('shared.product_serial_numbers.columns.warehouse')}>
              {detail.inventory?.warehouse?.name ?? placeholder}
            </Descriptions.Item>
            <Descriptions.Item label={t('shared.product_serial_numbers.columns.batch')}>
              {detail.inventory?.batch ?? placeholder}
            </Descriptions.Item>
            <Descriptions.Item label={t('shared.product_serial_numbers.columns.inventory_type')}>
              {detail.inventory?.inventory_type?.name ?? placeholder}
            </Descriptions.Item>
            <Descriptions.Item label={t('shared.product_serial_numbers.columns.updated_at')}>
              {formatDateTime(detail.updated_at)}
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '16px 0' }} />

          <Title level={5} style={{ marginTop: 0 }}>
            {t('shared.product_serial_numbers.drawer.sections.timeline')}
          </Title>
          <ProductSerialNumberTimeline role={role} events={detail.timeline} />
        </>
      )}
    </Drawer>
  );
};

export default ProductSerialNumberDrawer;
